import React, { Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { loadClarityAsync, DynamicToaster, DynamicSonner } from "@/lib/dynamic-imports";
import { registerServiceWorker } from "@/lib/service-worker";
import { PerformanceProvider } from "@/components/optimizations/PerformanceProvider";
import InstagramOptimizer from "@/components/optimizations/InstagramOptimizer";

// Dynamic import for PageAnalytics
const PageAnalytics = React.lazy(() => import("@/components/PageAnalytics"));

// Lazy load pages for better performance
const Home = React.lazy(() => import("./pages/Home"));
const Family = React.lazy(() => import("./pages/Family"));
const CorporateAdhesion = React.lazy(() => import("./pages/CorporateAdhesion"));
const Enterprise = React.lazy(() => import("./pages/Enterprise"));
const Students = React.lazy(() => import("./pages/Students"));
const Senior = React.lazy(() => import("./pages/Senior"));
const InstagramLanding = React.lazy(() => import("./pages/InstagramLanding"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading component for Suspense fallback
const PageLoadingFallback = () => (
  <div className="min-h-screen bg-background">
    <div className="container mx-auto px-4 py-20">
      <div className="space-y-8">
        <Skeleton className="h-96 w-full rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="space-y-4">
              <Skeleton className="h-56 w-full rounded-lg" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-9 w-32 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const App = () => {
  // Inicializar Microsoft Clarity e Service Worker
  useEffect(() => {
    try {
      // Carregar Clarity de forma assíncrona
      loadClarityAsync();
      
      // Registrar Service Worker em produção
      if (import.meta.env.PROD) {
        registerServiceWorker({
          enabled: true,
          updateCheckInterval: 300000 // 5 minutos
        }).then(registered => {
          if (registered) {
            console.log('[App] Service Worker registrado com sucesso');
          }
        });
      }
    } catch (error) {
      console.error('Erro ao inicializar serviços:', error);
    }
  }, []);

  return (
    <PerformanceProvider debugMode={import.meta.env.DEV}>
      <InstagramOptimizer>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Suspense fallback={null}>
              <DynamicToaster />
            </Suspense>
            <Suspense fallback={null}>
              <DynamicSonner />
            </Suspense>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Suspense fallback={null}>
                <PageAnalytics>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/plano-familia" element={<Family />} />
                      <Route path="/plano-empresa" element={<Enterprise />} />
                      <Route path="/coletivo-adesao" element={<CorporateAdhesion />} />
                      <Route path="/plano-estudantes" element={<Students />} />
                      <Route path="/med-senior" element={<Senior />} />
                      {/* Instagram Landing Pages */}
                      <Route path="/instagram" element={<InstagramLanding />} />
                      <Route path="/ig" element={<InstagramLanding />} />
                      <Route path="/insta" element={<InstagramLanding />} />
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </PageAnalytics>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </InstagramOptimizer>
    </PerformanceProvider>
  );
};

export default App;
